import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Music,
  Users,
  DollarSign,
  BarChart3,
  Bell,
  Monitor,
  FileText,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  X,
} from 'lucide-react';
import { useSidebarStore } from '@/store/sidebarStore';
import { useAuthStore } from '@/store/authStore';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/utils/cn';
import type { RouteKey } from '@/types/rbac';
import { ROUTE_PATHS } from '@/types/rbac';
import type { LucideIcon } from 'lucide-react';

interface NavItem {
  key: RouteKey;
  icon: LucideIcon;
}

const navItems: NavItem[] = [
  { key: 'dashboard', icon: LayoutDashboard },
  { key: 'packages', icon: Music },
  { key: 'users', icon: Users },
  { key: 'finance', icon: DollarSign },
  { key: 'analytics', icon: BarChart3 },
  { key: 'notifications', icon: Bell },
  { key: 'banners', icon: Monitor },
  { key: 'audit', icon: FileText },
  { key: 'settings', icon: Settings },
];

/** Role display names */
const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  CONTENT_ADMIN: 'Content Admin',
  FINANCE_ADMIN: 'Finance Admin',
  SUPPORT: 'Support',
};

export function Sidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { collapsed, toggle, mobileOpen, closeMobile } = useSidebarStore();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const { canAccess } = usePermissions();

  // Close mobile sidebar on route change
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  // Close mobile sidebar on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileOpen) closeMobile();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileOpen, closeMobile]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleNav = (path: string) => {
    navigate(path);
    closeMobile();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials =
    user?.full_name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) ?? 'AD';

  // Filter nav items based on RBAC
  const visibleItems = navItems.filter((item) => canAccess(item.key));

  const sidebarContent = (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-white/[0.06] bg-sidebar-bg',
        // Desktop: fixed sidebar
        'lg:fixed lg:bottom-0 lg:left-0 lg:top-0 lg:z-50',
        // Desktop width
        collapsed ? 'lg:w-[72px]' : 'lg:w-[260px]',
        // Mobile: full width in overlay
        'w-[280px] lg:transition-all lg:duration-300',
      )}
    >
      {/* Logo */}
      <div className="flex min-h-[80px] items-center justify-between border-b border-white/[0.06] px-6">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-teal to-deep-blue">
            <Music className="h-5 w-5 text-white" />
          </div>
          {(!collapsed || mobileOpen) && (
            <div className="flex flex-col">
              <div className="font-heading text-base font-extrabold tracking-tight">
                <span className="text-white">SYNAP</span>
                <span className="text-teal">SOUNDS</span>
              </div>
              <span className="text-[10px] tracking-wider text-white/35">ADMIN PANEL</span>
            </div>
          )}
        </div>

        {/* Desktop: collapse/expand */}
        <button
          onClick={toggle}
          className="hidden text-white/30 hover:text-white/60 lg:flex"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>

        {/* Mobile: close button */}
        <button
          onClick={closeMobile}
          className="flex text-white/30 hover:text-white/60 lg:hidden"
          aria-label="Close sidebar"
        >
          <X size={20} />
        </button>
      </div>

      {/* Nav */}
      <nav role="navigation" aria-label="Menu principal" className="flex flex-1 flex-col gap-1 overflow-y-auto p-3">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          const path = ROUTE_PATHS[item.key];
          const active = isActive(path);
          const showLabel = !collapsed || mobileOpen;

          return (
            <button
              key={item.key}
              onClick={() => handleNav(path)}
              title={collapsed && !mobileOpen ? t(`sidebar.${item.key}`) : undefined}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'relative flex items-center gap-3.5 rounded-[10px] px-5 py-3 text-sm font-normal text-white/55 transition-all',
                collapsed && !mobileOpen && 'justify-center px-4',
                active && 'bg-teal/[0.09] font-semibold text-teal',
                !active && 'hover:bg-sidebar-hover hover:text-white/90',
              )}
            >
              {active && (
                <span className="absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r bg-teal" />
              )}
              <Icon size={20} className="flex-shrink-0" />
              {showLabel && <span>{t(`sidebar.${item.key}`)}</span>}
            </button>
          );
        })}
      </nav>

      {/* User section */}
      <div className="border-t border-white/[0.06] px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-coral to-teal text-sm font-bold text-white">
            {initials}
          </div>
          {(!collapsed || mobileOpen) && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-semibold text-white">
                {user?.full_name ?? 'Admin'}
              </p>
              <p className="text-[11px] text-white/40">
                {user?.role ? (ROLE_LABELS[user.role] ?? user.role) : 'Admin'}
              </p>
            </div>
          )}
          {(!collapsed || mobileOpen) && (
            <button
              onClick={handleLogout}
              className="flex-shrink-0 text-white/30 transition-colors hover:text-danger"
              title={t('auth.logout')}
            >
              <LogOut size={16} />
            </button>
          )}
        </div>
      </div>
    </aside>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden lg:block">{sidebarContent}</div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={closeMobile}
            aria-hidden
          />
          {/* Sidebar overlay */}
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">{sidebarContent}</div>
        </>
      )}
    </>
  );
}
